import { Box, Grid } from '@mui/material';
import { useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { IJob, IJobCandidateInteraction } from 'wisestep-api';
import { WsListingLoader } from 'wisestep-base';

import WsJobsCrmSmartSourceAdvanceFilter from '../../../../components/filters/jobsCrmSmartSource';
import JobsCrmSmartSourceAdvanceFiltersFormProps from '../../../../components/filters/models/JobsCrmSmartSourceAdvanceFiltersFormProps';
import CardWrapper from '../../../../features/automatedEmailCampaignSettings/childRoutes/CardWrapper';
import { paginationSettingsStore } from '../../../../utils/PaginationSettingStore';
import { FiltersTagProp } from '../../components/filterTagList/FiltersTagList';
import { getDefaultAdavnacedSetFilters } from '../../components/helper/JobsCrmHelper';
import JobEmailCampaignAnalytics from '../../components/jobEmailCampaignAnalytics';
import useJobCrmListing from '../../hooks/useJobCrmListing';
import {
  defaultAISuggestionsFiltersValues,
  FilterKey,
  getActiveFilters,
  getDefaultAISuggestionsPayload,
  getQueryAndFormValues,
  getUpdatedAISuggestionsPayload,
} from '../../utils/sourcingHelper';

import AICandidateListing from './AICandidateListing';
import AISuggestionHeader from './AISuggestionHeader';

const AISuggestions = ({ job }: { job: IJob }) => {
  const paginationData = paginationSettingsStore((state) => state.getPaginationSettings);
  const paginationSettings = paginationData();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryData = searchParams.get('q') ?? '';
  const [query, setQuery] = useState<IJobCandidateInteraction>(
    queryData
      ? JSON.parse(decodeURIComponent(queryData))
      : getDefaultAISuggestionsPayload(job, Number(paginationSettings.jobCrmPagination ?? 20))
  );

  const methods = useForm({
    defaultValues: queryData
      ? getDefaultAdavnacedSetFilters(query, job)
      : defaultAISuggestionsFiltersValues,
  });

  const { handleSubmit, reset, getValues } = methods;

  const [filterTags, setFilterTags] = useState<FiltersTagProp[]>(
    getActiveFilters(getValues(), job)
  );

  const { state: data, loading } = useJobCrmListing(query, job.id);

  const updateQuery = (updatedQuery: IJobCandidateInteraction) => {
    setQuery(updatedQuery);
    setSearchParams({ q: encodeURIComponent(JSON.stringify(updatedQuery)) });
  };

  const handleOnSubmit = (data: FieldValues) => {
    const updatedQuery = getUpdatedAISuggestionsPayload(data, query, job);
    setFilterTags(getActiveFilters(data as JobsCrmSmartSourceAdvanceFiltersFormProps, job));
    updateQuery(updatedQuery);
  };

  const handleReset = () => {
    reset(defaultAISuggestionsFiltersValues);
    setFilterTags(getActiveFilters(defaultAISuggestionsFiltersValues, job));
    const updatedQuery = getUpdatedAISuggestionsPayload(
      defaultAISuggestionsFiltersValues,
      query,
      job
    );
    updateQuery(updatedQuery);
  };

  const onPageChange = (_, value: number) => {
    const updatedQuery = { ...query, page: value };
    updateQuery(updatedQuery);
  };

  const onFilterTagRemoval = (id: string) => {
    const [key, index] = id.split('#');

    const { updatedFormValues, updatedQuery } = getQueryAndFormValues(
      key as FilterKey,
      query,
      getValues(),
      Number(index)
    );
    reset(updatedFormValues);
    setFilterTags(getActiveFilters(updatedFormValues, job));
    updateQuery(updatedQuery);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleOnSubmit)} onReset={handleReset}>
        <Grid display="flex" flexDirection="column" rowGap={2}>
          <CardWrapper sx={{ pt: 0 }}>
            <AISuggestionHeader
              query={query}
              filterTags={filterTags}
              onTagRemoval={onFilterTagRemoval}
            />
          </CardWrapper>
          <Grid container spacing={2}>
            <Grid item xs={9}>
              <Box width="100%" display="flex" flexDirection="column" rowGap={2}>
                <JobEmailCampaignAnalytics job={job} />
                {loading ? (
                  <WsListingLoader />
                ) : (
                  <AICandidateListing
                    job={job}
                    data={data}
                    pagination={{
                      size: Number(paginationSettings?.jobCrmPagination ?? 20),
                      pageNumber: query.page,
                    }}
                    onPageChange={onPageChange}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={3}>
              <WsJobsCrmSmartSourceAdvanceFilter job={job} />
            </Grid>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default AISuggestions;
